'use client';

import { useCallback, useRef, useState } from 'react';

type ManualSubmissionOptions = {
  /** The sitting was already over when this mounted, so there is nothing left to claim. */
  alreadyClaimed: boolean;
  /**
   * Tells the server the sitting is over. Rejecting hands the sitting back, unless the
   * clock ran out — which is not a decision left to the server.
   */
  onStartSubmitting?: () => Promise<void>;
  /** Opens the step where what was answered on paper is transcribed. */
  onEnter: () => void;
};

/**
 * Leaving a manual sitting for the transcription step, which happens once and only once.
 *
 * The claim is held in a ref rather than in state because neither stopping the countdown
 * nor disabling the button takes effect until React re-renders — until then every further
 * click would start the transition again.
 */
function useManualSubmission({
  alreadyClaimed,
  onStartSubmitting,
  onEnter,
}: ManualSubmissionOptions) {
  const claimedRef = useRef(alreadyClaimed);
  const [isStarting, setIsStarting] = useState(false);

  const startSubmitting = useCallback(async () => {
    if (!onStartSubmitting) {
      return true;
    }

    setIsStarting(true);

    try {
      await onStartSubmitting();
      return true;
    } catch {
      return false;
    } finally {
      setIsStarting(false);
    }
  }, [onStartSubmitting]);

  const enter = useCallback(
    async (outOfTime: boolean) => {
      if (claimedRef.current) {
        return;
      }

      claimedRef.current = true;

      if (outOfTime) {
        onEnter();
        void startSubmitting();
        return;
      }

      if (await startSubmitting()) {
        onEnter();
      } else {
        claimedRef.current = false;
      }
    },
    [onEnter, startSubmitting],
  );

  return { isStarting, enter };
}

export { useManualSubmission };
